/****************
 * THE LEVERAGE DIAL'S MARKS (Guillaume 11-02: "make it look a bit more like an
 * Odometer by adding bars and numbers (don't change his size)"). Twin of
 * mobile-app/src/lib/leverageDial.core.ts; both apps' LeverageGauge draw these
 * inside the same 160 x 55 dial.
 *
 * The arc is the half-ellipse centred on (80, 50), 70 wide and 40 high on each
 * side; the marks sit just inside it, a long bar with its number every 25x and
 * a short bar every 5x.
 ****************/

export interface DialMark {
  x1: number; y1: number; x2: number; y2: number;
  major: boolean;
  /** Only on a major bar: its number and where it is written. */
  label?: string;
  lx?: number; ly?: number;
}

const CX = 80;
const CY = 50;
const RX = 70;
const RY = 40;

/*
 * ONE SCALE FOR EVERYTHING (Guillaume 11-03, second time: "still not good"; at
 * 14.9x the orange arc stopped just past 1x while the needle pointed near 25x,
 * and the numbers bunched low on the left). Three scales were in use: the
 * marks and needle walked the ellipse's parameter, which on a 70 x 40 ellipse
 * is not the angle the eye sees, and the orange arc was a share of the arc's
 * LENGTH, a third scale. Now a value is an angle seen from the centre, spread
 * evenly over the half-turn; every mark, the needle and the arc's end sit on
 * that one ray, where it crosses the ellipse.
 */
/** Seen from the centre, the share f of the sweep: its direction and the ellipse's distance on it. */
const ray = (f: number) => {
  const phi = Math.PI * (1 - Math.max(0, Math.min(1, f)));
  const dx = Math.cos(phi), dy = -Math.sin(phi);
  const r = (RX * RY) / Math.hypot(RY * dx, RX * dy);
  return { dx, dy, r };
};
const at = (f: number, inset: number) => {
  const { dx, dy, r } = ray(f);
  return { x: CX + (r - inset) * dx, y: CY + (r - inset) * dy };
};

export function dialMarks(maxLev = 125): DialMark[] {
  const out: DialMark[] = [];
  for (let lev = 0; lev <= maxLev; lev += 5) {
    const major = lev % 25 === 0;
    const f = lev / maxLev;
    const a = at(f, 6);
    const b = at(f, major ? 12 : 9);
    const mark: DialMark = { x1: a.x, y1: a.y, x2: b.x, y2: b.y, major };
    if (major) {
      const l = at(f, 19);
      mark.label = `${lev === 0 ? 1 : lev}x`;
      mark.lx = l.x;
      mark.ly = l.y + 2;
    }
    out.push(mark);
  }
  return out;
}

/** The needle's tip: on the value's own ray, short enough to clear the numbers. */
export function needleTip(pct: number): { x: number; y: number } {
  const { dx, dy, r } = ray(pct / 100);
  const len = (r - 6) * 0.55;
  return { x: CX + len * dx, y: CY + len * dy };
}

/** The coloured arc, from the left end to where the value's ray meets the ellipse. Empty at 0. */
export function valueArcPath(pct: number): string {
  if (!(pct > 0)) return '';
  const e = at(pct / 100, 0);
  return `M ${CX - RX} ${CY} A ${RX} ${RY} 0 0 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
}
